import { useCallback, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, Typography, CircularProgress, Stack, Box } from '@mui/material';

import { Page, useAppLayoutContextConsumer, snackbar } from '@occc-app/design-system';
import { LinkItem, Button } from '@occc/ui-design-system';
import { useIsUserPermittedTo, Feature } from '@occc/utils-access-control';
import { useGetOfferById } from '@occc-app/dataAccessLayer/offer/queries';
import {
  useDeleteKioskAdSetMutation,
  usePublishKioskAdSetMutation,
  useArchiveKioskAdSetMutation,
} from '@occc/graphql';

import {
  AdSetActions,
  AdSetDetailsCard,
  AdSetAssetPreviewCard,
  AdSetStatusChip,
} from './components';

import { AdSetsDeleteConfirmDialog } from '../../components/ad-sets-delete-confirm-dialog.component';
import { AdSetPublishDialog } from '../../components/ad-set-publish-dialog.component';
import { AdSetArchiveDialog } from '../../components/ad-set-archive-dialog.component';
import { routeCreator } from '../../touchpoint.router';
import { useGetKioskAdSet } from '../../hooks/use-get-ad-set.hook';
import {
  validateAdSetForPublish,
  isAdSetValidForPublish,
} from '../../utils/ad-set-publish-validation';
import { getOfferDetailsUrl } from '../../utils/offer-url';
import {
  AD_SET_DELETE_SUCCESS_MESSAGE,
  AD_SET_DELETE_ERROR_MESSAGE,
  AD_SET_PUBLISH_SUCCESS_MESSAGE,
  AD_SET_PUBLISH_ERROR_MESSAGE,
  AD_SET_ARCHIVE_SUCCESS_MESSAGE,
  AD_SET_ARCHIVE_ERROR_MESSAGE,
} from '../../touchpoint.constants';

const PAGE_TITLE = 'Ad Set Details';

type DialogType = 'delete' | 'publish' | 'archive' | null;

export const AdSetsDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isUserPermittedToEditAdSet = useIsUserPermittedTo(Feature.TOUCHPOINT_AD_SET_EDIT);
  const [activeDialog, setActiveDialog] = useState<DialogType>(null);

  const { loading, error, adSet, refetch } = useGetKioskAdSet({ id: id || '' });
  const [deleteKioskAdSet, { loading: loadingDelete }] = useDeleteKioskAdSetMutation();
  const [publishKioskAdSet, { loading: loadingPublish }] = usePublishKioskAdSetMutation();
  const [archiveKioskAdSet, { loading: loadingArchive }] = useArchiveKioskAdSetMutation();

  const { data: offerData } = useGetOfferById(adSet?.offerId || undefined, undefined, {
    skip: !adSet?.offerId,
  });

  const offer = useMemo(() => {
    if (!offerData) return null;

    return {
      id: offerData.id,
      name: offerData.name,
      url: getOfferDetailsUrl(offerData.id, offerData.engagementType, offerData.campaign?.uuid),
    };
  }, [offerData]);

  const validationErrors = useMemo(() => {
    if (!adSet) return null;
    return validateAdSetForPublish(adSet);
  }, [adSet]);

  const breadcrumbLinks: LinkItem[] = useMemo(
    () => [
      {
        to: routeCreator.adSetsList(),
        label: 'Touchpoint',
      },
      {
        to: routeCreator.adSetsList(),
        label: 'Ad Sets',
      },
    ],
    [],
  );

  useAppLayoutContextConsumer(breadcrumbLinks);

  const handleBack = useCallback(() => {
    navigate(routeCreator.adSetsList());
  }, [navigate]);

  const closeDialog = useCallback(() => {
    setActiveDialog(null);
  }, []);

  const handleDelete = useCallback(() => {
    setActiveDialog('delete');
  }, []);

  const handlePublish = useCallback(() => {
    setActiveDialog('publish');
  }, []);

  const handleArchive = useCallback(() => {
    setActiveDialog('archive');
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!id) return;

    await deleteKioskAdSet({
      variables: { id },
      onCompleted: () => {
        snackbar.success(AD_SET_DELETE_SUCCESS_MESSAGE);
        closeDialog();
        navigate(routeCreator.adSetsList());
      },
      onError: () => {
        snackbar.error(AD_SET_DELETE_ERROR_MESSAGE);
        closeDialog();
      },
    });
  }, [deleteKioskAdSet, id, navigate, closeDialog]);

  const handleConfirmPublish = useCallback(async () => {
    if (!id || !adSet || !validationErrors) return;

    if (!isAdSetValidForPublish(validationErrors)) {
      return;
    }

    await publishKioskAdSet({
      variables: { id },
      onCompleted: () => {
        snackbar.success(AD_SET_PUBLISH_SUCCESS_MESSAGE);
        closeDialog();
        refetch();
      },
      onError: () => {
        snackbar.error(AD_SET_PUBLISH_ERROR_MESSAGE);
        closeDialog();
      },
    });
  }, [publishKioskAdSet, id, adSet, validationErrors, closeDialog, refetch]);

  const handleConfirmArchive = useCallback(async () => {
    if (!id) return;

    await archiveKioskAdSet({
      variables: { id },
      onCompleted: () => {
        snackbar.success(AD_SET_ARCHIVE_SUCCESS_MESSAGE);
        closeDialog();
        refetch();
      },
      onError: () => {
        snackbar.error(AD_SET_ARCHIVE_ERROR_MESSAGE);
        closeDialog();
      },
    });
  }, [archiveKioskAdSet, id, closeDialog, refetch]);

  if (!id) {
    return (
      <Page
        browserTabTitle={PAGE_TITLE}
        pageHeaderProps={{
          title: PAGE_TITLE,
          subtitle: 'Error',
        }}
      >
        <Card>
          <CardContent>
            <Typography color='error'>Ad Set ID is required</Typography>
            <Button onClick={handleBack} sx={{ mt: 2 }}>
              Back to Ad Sets
            </Button>
          </CardContent>
        </Card>
      </Page>
    );
  }

  if (loading) {
    return (
      <Page
        browserTabTitle={PAGE_TITLE}
        pageHeaderProps={{
          title: PAGE_TITLE,
          subtitle: 'Loading...',
        }}
      >
        <Card>
          <CardContent>
            <CircularProgress />
          </CardContent>
        </Card>
      </Page>
    );
  }

  if (error || !adSet) {
    return (
      <Page
        browserTabTitle={PAGE_TITLE}
        pageHeaderProps={{
          title: PAGE_TITLE,
          subtitle: 'Error',
        }}
      >
        <Card>
          <CardContent>
            <Typography color='error'>{error?.message || 'Ad Set not found'}</Typography>
            <Button onClick={handleBack} sx={{ mt: 2 }}>
              Back to Ad Sets
            </Button>
          </CardContent>
        </Card>
      </Page>
    );
  }

  const actions = isUserPermittedToEditAdSet && (
    <AdSetActions
      editUrl={routeCreator.adSetsUpdate(id)}
      status={adSet.status}
      onDelete={handleDelete}
      onPublish={handlePublish}
      onArchive={handleArchive}
    />
  );

  return (
    <>
      <Page
        browserTabTitle={`${adSet.name || PAGE_TITLE}`}
        actions={actions}
        pageHeaderProps={{
          title: adSet.name || PAGE_TITLE,
          subtitle: adSet.description || '',
          status: (
            <Box sx={{ mt: 1 }}>
              <AdSetStatusChip status={adSet.status} />
            </Box>
          ),
        }}
      >
        <Stack spacing={2}>
          <AdSetDetailsCard
            offer={offer ?? undefined}
            kiosks={adSet.kiosks}
            schedules={adSet.schedules}
          />
          {adSet.asset?.imageSet && <AdSetAssetPreviewCard imageSet={adSet.asset.imageSet} />}
        </Stack>
      </Page>

      {activeDialog === 'delete' && (
        <AdSetsDeleteConfirmDialog
          onClose={closeDialog}
          onConfirm={handleConfirmDelete}
          loading={loadingDelete}
          adSetName={adSet.name}
        />
      )}

      {activeDialog === 'publish' && (
        <AdSetPublishDialog
          onClose={closeDialog}
          onConfirm={handleConfirmPublish}
          loading={loadingPublish}
          adSetName={adSet.name}
          validationErrors={validationErrors}
        />
      )}

      {activeDialog === 'archive' && (
        <AdSetArchiveDialog
          onClose={closeDialog}
          onConfirm={handleConfirmArchive}
          loading={loadingArchive}
          adSetName={adSet.name}
        />
      )}
    </>
  );
};
